/**
 * usePlacementKundenfreigabe — TanStack Query hooks for the Ausschreibungs-Screen Tab 4c
 * (Kundenfreigabe + Kundenentscheid + Promotion, Prozessschritte 13/14; P2 Placement Slice 12).
 *
 * Server state = TanStack Query ONLY. live mode hits apiClient.placements.*Kundenfreigabe; mock mode
 * resolves a fixture (Helvetia Lauf 1 released, zugestellt via portal, no decision yet). The server
 * owns every gate; the UI only triggers + renders. While a send is running the query refetches every 5 s.
 *
 * [Source: apps/web/src/features/placements/usePlacementVergleich.ts] (live/mock + qk + invalidate idiom)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import {
  CUSTOMER_DELIVERY_CHANNEL_LABELS,
  CUSTOMER_DELIVERY_LOG_ART_LABELS,
  CUSTOMER_DELIVERY_STATUS_LABELS,
  type KundenentscheidErfassenBody,
  type KundenfreigabeKanalBody,
  type KundenfreigabeResponse,
  type KundenfreigabeZustellenBody,
} from '@brokly/shared';

function mockKundenfreigabe(id: string): KundenfreigabeResponse {
  return {
    placement_id: id,
    placement_number: 'PL-2026-000001',
    status: 'empfohlen',
    closure_status: 'offen',
    branchencode: '21000',
    freigabe: {
      report_id: 'rep-mock-1',
      lauf_nr: 1,
      freigegeben_at: '2026-10-02T09:00:00.000Z',
      freigegeben_by_name: 'Max Broker',
      empfehlung_offer_id: 'offd-helvetia',
      empfehlung_carrier_name: 'Helvetia',
      empfehlung_submission_number: 'SUB-2026-000484-HAFT',
      veraltet: null,
      docx_vorhanden: true,
    },
    zustellung: {
      id: 'cd-mock-1',
      report_id: 'rep-mock-1',
      lauf_nr: 1,
      status: 'zugestellt',
      status_label: CUSTOMER_DELIVERY_STATUS_LABELS.zugestellt,
      is_aktuell: true,
      channel: 'portal',
      channel_label: CUSTOMER_DELIVERY_CHANNEL_LABELS.portal,
      channel_quelle: 'kanalsteuerung',
      channel_changed_at: null,
      channel_changed_by_name: null,
      kanalwechsel_count: 0,
      empfehlung_offer_id: 'offd-helvetia',
      empfehlung_carrier_name: 'Helvetia',
      empfehlung_submission_number: 'SUB-2026-000484-HAFT',
      docx: { filename: 'Offertvergleich.docx', sha256: 'a'.repeat(64), datei_url: `/placements/${id}/vergleich/rep-mock-1/export/datei` },
      sent_at: '2026-10-03T10:00:00.000Z',
      last_sent_at: '2026-10-03T10:00:00.000Z',
      sent_by_name: 'Max Broker',
      versand_count: 1,
      versand_laeuft: false,
      versand_gesperrt: false,
      nachtragen_moeglich: false,
      letzter_versandfehler: null,
      letzter_versandfehler_at: null,
      customer_response_channel: null,
      approved_at: null,
      approved_offer_id: null,
      hinweis: null,
      log: [
        {
          id: 'cdl-mock-1',
          art: 'versand',
          art_label: CUSTOMER_DELIVERY_LOG_ART_LABELS.versand,
          channel: 'portal',
          channel_von: null,
          channel_nach: null,
          grund: null,
          empfaenger: null,
          created_by_name: 'Max Broker',
          created_at: '2026-10-03T10:00:00.000Z',
        },
      ],
      created_at: '2026-10-03T10:00:00.000Z',
      updated_at: '2026-10-03T10:00:00.000Z',
    },
    entscheide: [],
    zustellen_erlaubt: false,
    sperrgrund: 'zustellung_offen',
    kanal_vorschlag: 'portal',
    kanal_quelle: 'kanalsteuerung',
    kanal_hinweis: null,
    portal_verfuegbar: true,
    kunde_email: 'info@mueller-ag.example',
    kunde_name: 'Mueller AG',
    kanalwechsel_erlaubt: true,
    erneut_senden_erlaubt: true,
    entscheid_erfassen_erlaubt: true,
    offerten: [
      { offer_document_id: 'offd-helvetia', submission_number: 'SUB-2026-000484-HAFT', carrier_name: 'Helvetia', rang: 1, score_total: 88.2, praemie_jahr: 19200, version_no: 1, ist_empfehlung: true },
      { offer_document_id: 'offd-axa', submission_number: 'SUB-2026-000482-HAFT', carrier_name: 'AXA', rang: 2, score_total: 81.5, praemie_jahr: 18400, version_no: 1, ist_empfehlung: false },
      { offer_document_id: 'offd-zurich', submission_number: 'SUB-2026-000483-HAFT', carrier_name: 'Zurich', rang: 3, score_total: 64.0, praemie_jahr: 16900, version_no: 1, ist_empfehlung: false },
    ],
  };
}

export function usePlacementKundenfreigabe(id: string, enabled = true) {
  const live = isLiveData();
  return useQuery<KundenfreigabeResponse>({
    queryKey: qk.placementKundenfreigabe(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getKundenfreigabe(id) : Promise.resolve(mockKundenfreigabe(id))),
    enabled,
    refetchInterval: (query) => (live && query.state.data?.zustellung?.versand_laeuft ? 5000 : false),
    ...(live ? {} : { initialData: () => mockKundenfreigabe(id), staleTime: Infinity }),
  });
}

function useKundenfreigabeInvalidate(id: string) {
  const qc = useQueryClient();
  return (opts: { decision?: boolean } = {}) => {
    void qc.invalidateQueries({ queryKey: ['placements', 'kundenfreigabe', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'vergleich', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'offerten', id] });
    if (opts.decision) {
      void qc.invalidateQueries({ queryKey: ['placements', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['placements', 'list'] });
      void qc.invalidateQueries({ queryKey: ['placements', 'submissions', id] });
    }
  };
}

export function useZustellenKundenfreigabe(id: string) {
  const live = isLiveData();
  const invalidate = useKundenfreigabeInvalidate(id);
  return useMutation({
    mutationFn: (body: KundenfreigabeZustellenBody = {}) =>
      live ? apiClient.placements.zustellenKundenfreigabe(id, body) : Promise.resolve(mockKundenfreigabe(id)),
    onSuccess: () => invalidate(),
  });
}

export function useKanalKundenfreigabe(id: string) {
  const live = isLiveData();
  const invalidate = useKundenfreigabeInvalidate(id);
  return useMutation({
    mutationFn: (body: KundenfreigabeKanalBody) =>
      live ? apiClient.placements.kanalKundenfreigabe(id, body) : Promise.resolve(mockKundenfreigabe(id)),
    onSuccess: () => invalidate(),
  });
}

export function useErneutSendenKundenfreigabe(id: string) {
  const live = isLiveData();
  const invalidate = useKundenfreigabeInvalidate(id);
  return useMutation({
    mutationFn: () => (live ? apiClient.placements.erneutSendenKundenfreigabe(id) : Promise.resolve(mockKundenfreigabe(id))),
    onSuccess: () => invalidate(),
  });
}

/** PR #43 wave 2 (#5): resolve an interrupted Kundenfreigabe send (nachtragen / freigeben). */
export function useVersandAufloesenKundenfreigabe(id: string) {
  const live = isLiveData();
  const invalidate = useKundenfreigabeInvalidate(id);
  return useMutation({
    mutationFn: (aktion: 'nachtragen' | 'freigeben') => (live ? apiClient.placements.versandAufloesenKundenfreigabe(id, aktion) : Promise.resolve(mockKundenfreigabe(id))),
    onSuccess: () => invalidate(),
  });
}

export function useEntscheidKundenfreigabe(id: string) {
  const live = isLiveData();
  const invalidate = useKundenfreigabeInvalidate(id);
  return useMutation({
    mutationFn: (body: KundenentscheidErfassenBody) =>
      live ? apiClient.placements.entscheidKundenfreigabe(id, body) : Promise.resolve(mockKundenfreigabe(id)),
    onSuccess: () => invalidate({ decision: true }),
  });
}
